import React from "react"
import ReactMarkdown from "react-markdown"

const ChangelogEntry = ({ date, title, changes }) => {
	const getIcon = type => {
		switch (type) {
			case "new":
				return <span className="text-green-500">🚀</span>
			case "fix":
				return <span className="text-red-500">🐛</span>
			case "improvement":
				return <span className="text-purple-500">💡</span>
			case "update":
				return <span className="text-blue-500">🔄</span>
			default:
				return null
		}
	}

	return (
		<div className="mb-12">
			<div className="mb-4">
				<span className="text-sm text-gray-500">{date}</span>
				<h2 className="text-2xl font-bold mt-1">{title}</h2>
			</div>
			<div className="space-y-4">
				{changes.map((change, index) => (
					<div key={index} className="flex items-start space-x-3">
						<div className="mt-1">{getIcon(change.type)}</div>
						<div>
							<h3 className="font-bold">{change.title}</h3>
							{change.image && (
								<video
									autoPlay
									loop
									muted
									playsInline
									className="w-full mx-auto my-8 rounded-lg block shadow-lg"
									style={{ imageRendering: "crisp-edges" }}
									onLoadedData={e => e.target.play()}
									onCanPlay={e => e.target.play()}
									ref={el =>
										el &&
										el
											.play()
											.catch(e => console.log("Video autoplay failed:", e))
									}
								>
									<source src={change.image} type="video/mp4" />
									Your browser does not support the video tag.
								</video>
							)}

							<div className="prose prose-slate prose-md text-gray-600 prose-li:text-gray-600">
								<ReactMarkdown>{change.description}</ReactMarkdown>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

const WhatsNew = () => {
	const changelog = [
		{
			date: "May 2025",
			title: "Charge Nurse Suggestions & Improvements",
			changes: [
				{
					type: "improvement",
					title: "Handling of Last-Minute Reschedules & Add-Ons",
					description: `
- We've improved how we handle patients who were rescheduled close to their appointment time in Cerner
- These patients may appear as "add-ons" in Oncare due to data transfer timing from Cerner
- The system will attempt to recommend eligible nurses for these add-ons when possible
- We've updated the prompts for clarity:
  - Old: *"An error occurred while trying to find assignments for add-on patients"*
  - New: *"Manual Assignment Required for Add-On Patients"*
- Added safeguard: Users now receive a reminder if they try to exit without assigning all patients: *"You must manually assign all patients before closing this dialog"*
					`
				},
				{
					type: "new",
					title: "Clinic Appointment Times Now Visible",
					description: `
- Clinic appointment times are now visible when hovering over patients in the Gantt View
- They're also available in the List View for quick reference
					`
				},
				{
					type: "new",
					title: "Customizable Columns, Filters & Saved Views in List View",
					image: "https://i.imgur.com/WLYeOYS.mp4",
					description: `
- Full customization of the List View is now available:
  - Select any patient fields to display (clinic visit time, referring department, acuity, etc.)
  - Filter on any field to focus on specific patient groups
  - Sort columns in your preferred direction
- All preferences (selected columns, filters, and sort order) are automatically saved to your browser
- Advanced search capabilities now let you filter for specific scenarios, such as patients with insufficient gaps between scheduled start times and clinic visits
					`
				},
				{
					type: "fix",
					title: "Various Fixes & Improvements",
					image: "https://i.imgur.com/Sq5oNe1.mp4",
					description: `
- Grid View now properly updates when a nurse is removed
- Nurses with no completed patients can now be fully removed from the IC Tracker in one step
- Inactive nurses (removed nurses or those that are not on shift) no longer appear on the homepage
- Patient Location Locking: Locations entered in the tracking shell can't be overwritten from patient details view
- Lunch Break Indicators Improved: "Late lunch" warnings only appear after the break has been officially logged
- Saturday Printout Fix: Corrected an issue where Monday's schedule would incorrectly display as "Saturday" on printouts generated on Saturdays
					`
				}
			]
		},
		{
			date: "April 2025",
			title: "New Release: Operational Reports",
			changes: [
				{
					type: "new",
					title: "Operational Reports",
					image: "https://i.imgur.com/njGcayq.mp4",
					description: `
- New dashboard with comprehensive metrics to monitor infusion center performance
- Real-time visibility into patient flow metrics, helping staff quickly identify bottlenecks
- Key metrics include:
  - Patient volume, Patient wait times, and punctuality trends by day of week and time slot
  - Nurse workload distribution and capacity analysis
  - Treatment Duration Analysis to analyze block sizes
					`
				}
			]
		},
		{
			date: "March 2025",
			title: "Charge Nurse Retreat Improvements",
			changes: [
				{
					type: "improvement",
					title: "Nurse Experience Enhancements",
					description: `
- Seamless Communication Across Shifts: Daily huddle messages now automatically carry over to the next day, ensuring no important updates are lost! 🔄
- Enhanced IC Tracker: Now featuring dedicated On Call Supervisor field for better coordination and oversight 📋
- Smart Notification Handoff: When taking over as CN, instantly see all important notifications from the previous CN - no more missed updates! 🔔
- Lightning-Fast Updates: Experience real-time team communication with notifications now appearing in just 30 seconds! ⚡
- Streamlined Assignment Planning: CNs can now easily hide cancellations when planning tomorrow's assignments for a cleaner view 👀
- More Accurate Visit End Times: Removed the prediction of visit end times, showing actual visit durations instead ⏰
					`
				}
			]
		},
		{
			date: "February 2025",
			title: "Platform Goes Live",
			changes: [
				{
					type: "improvement",
					title: "Algorithm Improvements",
					description: `
- We've made our nurse scheduling even better:
  - Patients are now more evenly distributed between nurses to ensure folks recieve enough early morning patients
  - The workload stays more balanced throughout each shift, making everyone's day smoother
  `
				},
				{
					type: "new",
					title: "Enhanced Grid View Features",
					image: "https://i.imgur.com/3uy3ToT.mp4",
					description: `
- We've made it easier to spot add-on patients! Their names now appear in red in the grid view, helping charge nurses quickly identify which patients were added after the initial schedule was created.
- You can now see nurse zone assignments at a glance! The grid view shows which zones nurses are working in based on their patients' assigned chairs.
- Moving nurses around is now as easy as drag and drop! Simply click and drag nurse cards to rearrange them in the grid view - perfect for quickly reorganizing your staff layout.
- New filtering options make it easier to focus on what matters! Toggle between:
  - Scheduled patients - see who's coming in
  - No Shows & Cancellations - track schedule changes
  - Completed visits - review finished appointments
					`
				},
				{
					type: "new",
					title: "Faster Assignment Editing",
					image: "https://i.imgur.com/4DP5jeu.mp4",
					description: `
- Making patient swaps is now a breeze! Just click on any patient and use our new swap button to quickly exchange patients between nurses or chairs. The swap dialogue lets you easily find the right patient by searching their name or filtering by acuity level.
					`
				},
				{
					type: "improvement",
					title: "General Improvements",
					image: "https://i.imgur.com/vGLhXmc.mp4",
					description: `
- We've supercharged our search feature to make your life easier!
  - Immediately locate team members
  - Quickly locate patients
  - Spot open chairs instantly
- No more manual data entry - chair info now syncs from the tracking shell
- Great news! Saturday assignments are now ready for you at 12 PM
- Your grid view printouts just got a beautiful makeover!
- Working faster than ever - all input fields are now pre-selected and ready to edit
- A cleaner tomorrow schedule view that automatically hides cancelled patients
- Finding nurses is easier with new alphabetical sorting in the Gantt chart view
					`
				},
				{
					type: "fix",
					title: "General Fixes",
					description: `
- LVNs now recieve assignments that factor in their eligibility criteria
- The "Assume Charge Nurse" button is new & improved:
  - Instant updates your permissions without needing to refresh the page
- Your role now displays correctly on your personal settings page
- Chair status indicators are fixed in the edit patient window:
  - See available chairs at a glance ✅
  - Easily spot busy chairs 🔄
- Removing nurses is smoother - they'll be fully removed when all their patients are cancelled 🧹
					`
				}
			]
		}
	]

	//   - Notifications awaiting decisions will automatically transfer to your notification center

	return (
		<div className="max-w-3xl mx-auto px-4 py-12">
			<div className="mb-12">
				<h1 className="text-4xl font-bold mb-4">What's New</h1>
				<p className="text-gray-600">
					Stay up to date with the latest features, improvements, and bug fixes in Oncare.
				</p>
			</div>

			<div className="space-y-8">
				{changelog.map((entry, index) => (
					<ChangelogEntry
						key={index}
						date={entry.date}
						title={entry.title}
						changes={entry.changes}
					/>
				))}
			</div>
		</div>
	)
}

export default WhatsNew
